import React from "react";
import { Route, type RouteProps, Redirect } from "react-router-dom";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAppContext } from "~/contexts/appContext";
import { broadcastLogout } from "~/utils/coordinateLogout";
import { hasTreasuryAccessOnAnyArena } from "~/utils/userRoles";
import { MissingPermissionState } from "./MissingPermissionState";

export const PrivateRoute: React.FC<RouteProps> = ({ children, ...routeProps }) => {
  const { user } = useAppContext();
  const clientLevelPermissionsEnabled = useFeatureFlag("treasuryClientLevelPermissions", false);
  const isAuthenticated = user?.id && user?.id !== -1;
  // The backend isTreasuryUser boolean only covers Treasury Admins; analysts
  // carry the "Treasury Analyst" role instead and must not be locked out here.
  const hasCanopyAccess =
    user.canAccessAdminArena ||
    user.isCanopyUser ||
    user.isComplianceUser ||
    user.isTreasuryUser ||
    (clientLevelPermissionsEnabled && hasTreasuryAccessOnAnyArena(user.roles));

  // has canopy access
  if (isAuthenticated && hasCanopyAccess) {
    return <Route {...routeProps}>{children}</Route>;
  }

  // has only kyc access, redirected back to kyc
  if (isAuthenticated && user.isKycUser && !hasCanopyAccess) {
    window.location.assign("/kyc");
    return;
  }

  // has no permissions
  if (isAuthenticated) {
    return <MissingPermissionState />;
  }

  const searchParams = new URLSearchParams(location.search);
  searchParams.append("path", location.pathname);
  broadcastLogout();

  return (
    <Route {...routeProps}>
      <Redirect to={{ pathname: "/login", search: `?${searchParams.toString()}` }} />
    </Route>
  );
};
